import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { BACKEND_URL as BASE_URL } from 'src/settings';
import { SSE } from 'sse.js';

interface CreateSSEOptions {
  endpoint: string;
  payload?: Record<string, unknown>;
  onMessage: (data: string) => void;
  onError?: (event?: Event) => void;
  onClose?: () => void;
  onSuccess?: () => void;
}

function createSSEConnection({
  endpoint,
  payload = {},
  onMessage,
  onError,
  onClose,
  onSuccess
}: CreateSSEOptions): SSE {
  const { authToken, selectedOrganization, impersonation } =
    useAuthStore.getState();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (authToken) {
    headers['Authorization'] = `Token ${authToken}`;
  }

  if (selectedOrganization && selectedOrganization.id) {
    headers['X-Organization-Id'] = selectedOrganization.id.toString();
  }

  if (impersonation && impersonation !== '0') {
    headers['Impersonation'] = impersonation;
  }

  const url = `${BASE_URL}/api${endpoint}`;

  let hasDoneEvent = false;

  const source = new SSE(url, {
    headers,
    method: 'POST',
    payload: JSON.stringify(payload),
    start: false
  });

  source.addEventListener('message', (event: Event) => {
    const data = (event as MessageEvent & { data: string }).data;

    if (data) {
      // Replace escaped newlines with actual newlines for markdown
      const processedData = data.replace(/\\n/g, '\n');

      onMessage(processedData);
    }
  });

  // Listen for 'done' event from server to detect stream completion
  source.addEventListener('done', () => {
    hasDoneEvent = true;
    if (onSuccess) {
      onSuccess();
    }
  });

  source.addEventListener('error', (event: Event) => {
    const readyState = (source as unknown as { readyState?: number })
      .readyState;

    // Log debug info to help diagnose browser-specific SSE issues (e.g. Firefox NS_BINDING_ABORTED)

    console.error('SSE connection error', {
      endpoint,
      hasDoneEvent,
      readyState,
      event
    });

    // In some browsers (Firefox), a benign close after a successful "done" event
    // can surface as an error. If we've already seen "done", treat this as a normal close.
    if (hasDoneEvent && readyState === 2) {
      if (onClose) {
        onClose();
      }

      return;
    }

    if (onError) {
      onError(event);
    }

    source.close();
  });

  if (onClose) {
    source.addEventListener('readystatechange', () => {
      // CLOSED state
      if (source.readyState === 2) {
        onClose();
      }
    });
  }

  return source;
}

export default createSSEConnection;
